import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getStudentDetails } from "../../../services/StudentPortal/StudentProfileService";
import { getSiblings } from "../../../services/StudentPortal/StudentSiblingService";
import { handleApiError } from "../../../utils/errorHandler";
import { STUDENT_STORAGE_KEYS } from "../../../services/StudentPortal/studentStorageKeys";

const StudentSessionContext = createContext(null);

const ACTIVE_CHILD_KEY = STUDENT_STORAGE_KEYS.ACTIVE_CHILD_ID;

/**
 * Loads the logged-in student's own details + sibling list once, and shares
 * them (plus the active-child switch and logout) with every StudentPortal
 * page, instead of each page reading localStorage / refetching on its own.
 */
export const StudentSessionProvider = ({ children }) => {
  const [student, setStudent] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [activeChildId, setActiveChildIdState] = useState(
    () => localStorage.getItem(ACTIVE_CHILD_KEY) || null
  );
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const loadSession = useCallback(async () => {
    setIsLoading(true);
    try {
      // Confirmed against the real backend (2026-10-22): unlike almost every
      // other student/* endpoint, student/details returns the student object
      // FLAT (response.data IS the student), not wrapped in {success, data}.
      const detailsResponse = await getStudentDetails();
      const studentData = detailsResponse?.data || null;
      setStudent(studentData);

      // Most other student/* endpoints need the "organization-id" and
      // "session-id" headers plus a "student_id" param (studentApi.js's
      // interceptor attaches all three automatically once these are in
      // localStorage), so they're stored here before anything else is
      // fetched — mirrors the staff app's organization-id/session-id header
      // pair in src/services/api.js.
      if (studentData?.organization_id) {
        localStorage.setItem(STUDENT_STORAGE_KEYS.ORGANIZATION_ID, studentData.organization_id);
      }
      if (studentData?.student_program?.session_id) {
        localStorage.setItem(STUDENT_STORAGE_KEYS.SESSION_ID, studentData.student_program.session_id);
      }
      if (studentData?.id) {
        localStorage.setItem(STUDENT_STORAGE_KEYS.ID, studentData.id);
      }

      // Siblings are fetched only after the above is in localStorage (it's
      // optional — an only child shouldn't block the portal if this 404s).
      const siblingsResponse = await getSiblings(studentData?.id).catch(() => null);
      const siblingList = siblingsResponse?.data?.data || [];
      setSiblings(Array.isArray(siblingList) ? siblingList : []);

      if (studentData?.id) {
        setActiveChildIdState((current) => {
          if (current) return current;
          localStorage.setItem(ACTIVE_CHILD_KEY, studentData.id);
          return studentData.id;
        });
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  // Swap the active child.
  // ASSUMPTION (flagged for confirmation against the real backend contract):
  // each sibling is its own `student_api` principal, so `student/sibling`
  // is assumed to return a token per sibling, and switching replaces the
  // active `studentToken`. If the backend instead scopes data by a shared
  // token + a child-id parameter, this is the one function to change.
  const setActiveChildId = useCallback(
    (childId, childToken) => {
      if (childToken) localStorage.setItem(STUDENT_STORAGE_KEYS.TOKEN, childToken);
      localStorage.setItem(ACTIVE_CHILD_KEY, childId);
      setActiveChildIdState(childId);
      loadSession();
    },
    [loadSession]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(STUDENT_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STUDENT_STORAGE_KEYS.USER);
    localStorage.removeItem(STUDENT_STORAGE_KEYS.ORGANIZATION_ID);
    localStorage.removeItem(STUDENT_STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(STUDENT_STORAGE_KEYS.ID);
    localStorage.removeItem(ACTIVE_CHILD_KEY);
    navigate("/student-portal/login");
  }, [navigate]);

  const value = useMemo(
    () => ({ student, siblings, activeChildId, setActiveChildId, isLoading, logout, reload: loadSession }),
    [student, siblings, activeChildId, setActiveChildId, isLoading, logout, loadSession]
  );

  return <StudentSessionContext.Provider value={value}>{children}</StudentSessionContext.Provider>;
};

export const useStudentSession = () => {
  const ctx = useContext(StudentSessionContext);
  if (!ctx) throw new Error("useStudentSession must be used within a StudentSessionProvider");
  return ctx;
};
